import React, { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, MenuItem, Tooltip } from '@mui/material';
import {
  AccountCircleRounded,
  ChatBubbleOutlineRounded,
  FolderOpenRounded,
  HelpOutlineRounded,
  KeyboardArrowDownRounded,
  LogoutRounded,
  MoreHorizRounded,
  OpenInNewRounded,
  SyncRounded,
} from '@mui/icons-material';
import { t } from '@lingui/core/macro';
import { Activity } from './animate-ui/icons/activity';
import { ExternalLink } from './animate-ui/icons/external-link';
import { Clapperboard } from './animate-ui/icons/clapperboard';
import { LayoutDashboard } from './animate-ui/icons/layout-dashboard';
import { Palette } from './animate-ui/icons/palette';
import { SlidersHorizontal } from './animate-ui/icons/sliders-horizontal';
import { Timer } from './animate-ui/icons/timer';
import { auth } from '../firebase';
import { getProjectRoleLabel, useProject } from '../contexts/ProjectContext';
import { isAdminUserEmail } from '../utils/adminAccess';
import { storeEditorMode, type EditorMode } from '../utils/editorLayout';
import LanguageSwitcher from './LanguageSwitcher';
import AppearanceToggle from './AppearanceToggle';
import '../App.css';
import type { ProjectSaveStatus } from '../utils/periodicProjectTableSaveQueue';

interface NavBarProps {
  saveStatus?: ProjectSaveStatus;
  onRetrySave?: () => void;
  editorMode?: EditorMode;
  onEditorModeChange?: (mode: EditorMode) => void;
  showCardTiming?: boolean;
  canEdit?: boolean;
  googleSheetsUrl?: string | null;
  onLoadGoogleSheets?: () => void | Promise<void>;
  isGoogleSheetsLoading?: boolean;
  renderBackupAction?: (closeMenu: () => void) => React.ReactNode;
}

interface NavigationItem {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onSelect: () => void;
  className?: string;
}

export default function NavBar({
  saveStatus = 'idle',
  onRetrySave,
  editorMode,
  onEditorModeChange,
  showCardTiming = false,
  canEdit = true,
  googleSheetsUrl,
  onLoadGoogleSheets,
  isGoogleSheetsLoading = false,
  renderBackupAction,
}: NavBarProps): React.ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [projectAnchor, setProjectAnchor] = useState<HTMLElement | null>(null);
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);
  const [isEditButtonHovered, setIsEditButtonHovered] = useState(false);
  const [isDesignButtonHovered, setIsDesignButtonHovered] = useState(false);
  const [isSheetButtonHovered, setIsSheetButtonHovered] = useState(false);
  const [isRendersButtonHovered, setIsRendersButtonHovered] = useState(false);
  const [isProjectSettingsHovered, setIsProjectSettingsHovered] = useState(false);
  const [isCardTimingHovered, setIsCardTimingHovered] = useState(false);
  const [isAdminHovered, setIsAdminHovered] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProject, setSelectedProject } = useProject();
  const isAdmin = isAdminUserEmail(user?.email);
  const projectMenuLabel = selectedProject
    ? (selectedProject.name.length > 20 ? `${selectedProject.name.slice(0, 20)}…` : selectedProject.name)
    : '';

  useEffect(() => onAuthStateChanged(auth, setUser), []);

  const selectRoute = (path: string): void => {
    navigate(path);
    setMoreAnchor(null);
  };
  const handleEditorNavigation = (mode: EditorMode): void => {
    storeEditorMode(mode);
    onEditorModeChange?.(mode);
    selectRoute('/app');
  };
  const handleLogout = async (): Promise<void> => {
    setAccountAnchor(null);
    await signOut(auth);
    navigate('/');
  };

  const primaryItems: NavigationItem[] = [
    { label: t`Edit`, icon: <LayoutDashboard size={17} animate={isEditButtonHovered} />, active: location.pathname === '/app' && editorMode === 'edit', onSelect: () => handleEditorNavigation('edit'), className: 'nav-edit-item' },
    { label: t`Design`, icon: <Palette size={17} animate={isDesignButtonHovered} />, active: location.pathname === '/app' && editorMode === 'design', onSelect: () => handleEditorNavigation('design'), className: 'nav-design-item' },
    { label: t`Google Sheet`, icon: <ExternalLink size={17} animate={isSheetButtonHovered} />, active: location.pathname === '/app/sheet', onSelect: () => selectRoute('/app/sheet'), className: 'nav-sheet-item' },
    { label: t`Renders`, icon: <Clapperboard size={17} animate={isRendersButtonHovered} />, active: location.pathname === '/app/renders', onSelect: () => selectRoute('/app/renders'), className: 'nav-renders-item nav-secondary-item' },
    { label: t`Project Settings`, icon: <SlidersHorizontal size={17} animate={isProjectSettingsHovered} />, active: location.pathname === '/app/settings', onSelect: () => selectRoute('/app/settings'), className: 'nav-project-settings-item nav-secondary-item' },
  ];
  if (showCardTiming && canEdit) {
    primaryItems.push({ label: t`Card Timing`, icon: <Timer size={17} animate={isCardTimingHovered} />, active: location.pathname === '/app/card-timing', onSelect: () => selectRoute('/app/card-timing'), className: 'nav-card-timing-item nav-secondary-item' });
  }
  if (isAdmin) {
    primaryItems.push({ label: t`Admin`, icon: <Activity size={17} animate={isAdminHovered} />, active: location.pathname === '/admin', onSelect: () => selectRoute('/admin'), className: 'nav-admin-item nav-secondary-item' });
  }
  const moreItems: NavigationItem[] = [
    { label: t`Google Sheet`, icon: <ExternalLink size={17} animateOnHover />, active: location.pathname === '/app/sheet', onSelect: () => selectRoute('/app/sheet') },
    { label: t`Renders`, icon: <Clapperboard size={17} animate={isRendersButtonHovered} />, active: location.pathname === '/app/renders', onSelect: () => selectRoute('/app/renders'), className: 'nav-renders-item' },
    { label: t`Project Settings`, icon: <SlidersHorizontal size={17} animate={isProjectSettingsHovered} />, active: location.pathname === '/app/settings', onSelect: () => selectRoute('/app/settings'), className: 'nav-project-settings-item' },
  ];
  if (showCardTiming && canEdit) {
    moreItems.push({ label: t`Card Timing`, icon: <Timer size={17} animate={isCardTimingHovered} />, active: location.pathname === '/app/card-timing', onSelect: () => selectRoute('/app/card-timing'), className: 'nav-card-timing-item' });
  }
  if (isAdmin) {
    moreItems.push({ label: t`Admin`, icon: <Activity size={17} animate={isAdminHovered} />, active: location.pathname === '/admin', onSelect: () => selectRoute('/admin'), className: 'nav-admin-item' });
  }

  return (
    <nav className="navbar workspace-navbar" aria-label={t`Main navigation`}>
      <div className="navbar-left">
        <div className="navbar-brand" aria-label="FinTitles">
          <img className="navbar-brand-logo" src="/shared/logos/Fintitles%20logo.png" alt="" aria-hidden="true" />
          <span className="navbar-title">FinTitles</span>
          <span className="navbar-beta-badge">{t`Beta`}</span>
        </div>
        <div className="workspace-nav-primary">
          {primaryItems.map((item) => (
            <Tooltip key={item.label} title={item.label}>
              <button
                type="button"
                className={`workspace-nav-button ${item.className || ''}${item.active ? ' is-active' : ''}`}
                aria-current={item.active ? 'page' : undefined}
                onClick={item.onSelect}
                onMouseEnter={() => {
                  if (item.className === 'nav-edit-item') setIsEditButtonHovered(true);
                  if (item.className === 'nav-design-item') setIsDesignButtonHovered(true);
                  if (item.className === 'nav-sheet-item') setIsSheetButtonHovered(true);
                  if (item.className?.includes('nav-renders-item')) setIsRendersButtonHovered(true);
                  if (item.className?.includes('nav-project-settings-item')) setIsProjectSettingsHovered(true);
                  if (item.className?.includes('nav-card-timing-item')) setIsCardTimingHovered(true);
                  if (item.className?.includes('nav-admin-item')) setIsAdminHovered(true);
                }}
                onMouseLeave={() => {
                  if (item.className === 'nav-edit-item') setIsEditButtonHovered(false);
                  if (item.className === 'nav-design-item') setIsDesignButtonHovered(false);
                  if (item.className === 'nav-sheet-item') setIsSheetButtonHovered(false);
                  if (item.className?.includes('nav-renders-item')) setIsRendersButtonHovered(false);
                  if (item.className?.includes('nav-project-settings-item')) setIsProjectSettingsHovered(false);
                  if (item.className?.includes('nav-card-timing-item')) setIsCardTimingHovered(false);
                  if (item.className?.includes('nav-admin-item')) setIsAdminHovered(false);
                }}
              >
                {item.icon}<span>{item.label}</span>
              </button>
            </Tooltip>
          ))}
          <Tooltip title={t`More workspace options`}>
            <button ref={moreButtonRef} type="button" className={`workspace-nav-button workspace-nav-more${moreItems.some((item) => item.active) ? ' is-active' : ''}`} aria-label={t`More workspace options`} aria-haspopup="menu" aria-expanded={Boolean(moreAnchor)} onClick={(event) => setMoreAnchor(event.currentTarget)}>
              <MoreHorizRounded /><span>{t`More`}</span>
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="navbar-right workspace-nav-utilities">
        {selectedProject && saveStatus !== 'idle' && <span className="workspace-save-status" role="status" aria-live="polite" aria-atomic="true">
          {saveStatus === 'pending' ? t`Pending changes` : saveStatus === 'saving' ? t`Saving…` : saveStatus === 'saved' ? t`Saved` : t`Save failed`}
          {saveStatus === 'failed' && onRetrySave && <> · <button type="button" onClick={onRetrySave}>{t`Retry`}</button></>}
        </span>}
        {onLoadGoogleSheets && (
          <Tooltip title={isGoogleSheetsLoading ? t`Loading from Google Sheets...` : t`Load from Google Sheets`}>
            <span className="workspace-load-tooltip">
              <button type="button" className="workspace-load-button" onClick={() => void onLoadGoogleSheets()} disabled={isGoogleSheetsLoading} aria-label={isGoogleSheetsLoading ? t`Loading from Google Sheets...` : t`Load from Google Sheets`}>
                <SyncRounded className={isGoogleSheetsLoading ? 'is-spinning' : ''} />
                <span>{isGoogleSheetsLoading ? t`Loading...` : t`Load`}</span>
              </button>
            </span>
          </Tooltip>
        )}
        {selectedProject && (
          <Tooltip title={selectedProject.name}>
            <button type="button" className="workspace-nav-button workspace-project-menu-button" aria-label={t`Project options`} aria-haspopup="menu" aria-expanded={Boolean(projectAnchor)} onClick={(event) => setProjectAnchor(event.currentTarget)}>
              <span className="workspace-project-menu-label">{projectMenuLabel}</span>
              <KeyboardArrowDownRounded aria-hidden="true" />
            </button>
          </Tooltip>
        )}
        <AppearanceToggle />
        <LanguageSwitcher />
        <Tooltip title={t`Account`}>
          <button type="button" className="workspace-icon-button" aria-label={t`Account`} aria-haspopup="menu" aria-expanded={Boolean(accountAnchor)} onClick={(event) => setAccountAnchor(event.currentTarget)}>
            <AccountCircleRounded />
          </button>
        </Tooltip>
      </div>

      <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => { setMoreAnchor(null); moreButtonRef.current?.focus(); }} MenuListProps={{ 'aria-label': t`More workspace options` }}>
        {moreItems.map((item) => <MenuItem key={item.label} selected={item.active} onClick={item.onSelect} onMouseEnter={() => { if (item.className === 'nav-renders-item') setIsRendersButtonHovered(true); if (item.className === 'nav-project-settings-item') setIsProjectSettingsHovered(true); if (item.className === 'nav-card-timing-item') setIsCardTimingHovered(true); if (item.className === 'nav-admin-item') setIsAdminHovered(true); }} onMouseLeave={() => { if (item.className === 'nav-renders-item') setIsRendersButtonHovered(false); if (item.className === 'nav-project-settings-item') setIsProjectSettingsHovered(false); if (item.className === 'nav-card-timing-item') setIsCardTimingHovered(false); if (item.className === 'nav-admin-item') setIsAdminHovered(false); }}>{item.icon}<span>{item.label}</span></MenuItem>)}
      </Menu>
      <Menu anchorEl={projectAnchor} open={Boolean(projectAnchor)} onClose={() => setProjectAnchor(null)} MenuListProps={{ 'aria-label': t`Project options` }}>
        {selectedProject && <MenuItem disabled><span>{selectedProject.name} · {getProjectRoleLabel(selectedProject.permission_tier)}</span></MenuItem>}
        <MenuItem onClick={() => { setSelectedProject(null); setProjectAnchor(null); }}><FolderOpenRounded /><span>{t`Change Project`}</span></MenuItem>
        {googleSheetsUrl && <MenuItem component="a" href={googleSheetsUrl} target="_blank" rel="noopener noreferrer"><OpenInNewRounded /><span>{t`Open in Google Sheets`}</span></MenuItem>}
        {renderBackupAction?.(() => setProjectAnchor(null))}
      </Menu>
      <Menu anchorEl={accountAnchor} open={Boolean(accountAnchor)} onClose={() => setAccountAnchor(null)} MenuListProps={{ 'aria-label': t`Account` }}>
        <MenuItem disabled><span>{user?.email ?? t`Guest`}</span></MenuItem>
        <MenuItem component="a" href="mailto:support@fintitles.com"><HelpOutlineRounded /><span>{t`Email support`}</span></MenuItem>
        <MenuItem onClick={() => selectRoute('/feedback')}><ChatBubbleOutlineRounded /><span>{t`Feedback`}</span></MenuItem>
        {user ? <MenuItem onClick={() => void handleLogout()}><LogoutRounded /><span>{t`Logout`}</span></MenuItem> : <MenuItem onClick={() => selectRoute('/login')}><AccountCircleRounded /><span>{t`Login / Sign Up`}</span></MenuItem>}
      </Menu>
    </nav>
  );
}
